import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../cx';

// 스크롤 맨 위로 — 긴 목록 화면의 보조 컨트롤 (D-09 §15.55, Figma PT8wG7aRcWg4CerPViHOI4 / 4410:3125).
// 스크롤 소유자는 화면마다 다르다(D-07 §21.4) — doc 은 Shell 의 main, fixed 는 .page-scroll 이다.
// scrollRef 를 주면 그 요소를 듣고, 없으면 자기 위의 가장 가까운 스크롤 조상을 찾는다(끝까지 없으면 window).
// 기준선 아래에서는 보이지도 포커스되지도 않는다(.is-off = visibility: hidden) — 전환을 살리려고 DOM 에는 남긴다.
// 토스트와 자리가 같아 토스트가 떠 있는 동안 위로 비키는 것은 CSS 가 맡는다(components.css `body:has(.toast-wrap .toast)`).
// contract 없는 손 작성 컴포넌트(Drawer.tsx와 동일 예외) — 클래스는 styles/components.css(.scroll-top).
export interface ScrollTopButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** 스크롤 소유자. 없으면 가장 가까운 스크롤 조상 → window 순으로 찾는다 */
  scrollRef?: React.RefObject<HTMLElement | null>;
  /** 이 값(px)을 넘으면 나타난다. 기본 400 */
  threshold?: number;
  /** 접근 이름. 기본 '맨 위로' */
  label?: string;
}

/** 자기 위로 올라가며 세로 스크롤을 가진 첫 조상을 찾는다. 없으면 null */
function findScroller(from: HTMLElement | null): HTMLElement | null {
  for (let el = from?.parentElement ?? null; el && el !== document.body; el = el.parentElement) {
    const oy = getComputedStyle(el).overflowY;
    if (oy === 'auto' || oy === 'scroll') return el;
  }
  return null;
}

export const ScrollTopButton = React.forwardRef<HTMLButtonElement, ScrollTopButtonProps>(function ScrollTopButton(
  { scrollRef, threshold = 400, label = '맨 위로', className = '', type = 'button', onClick, ...props },
  ref,
) {
  const selfRef = useRef<HTMLButtonElement>(null);
  const scroller = useRef<HTMLElement | Window | null>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el: HTMLElement | Window = scrollRef?.current ?? findScroller(selfRef.current) ?? window;
    scroller.current = el;
    const read = () => (el === window ? window.scrollY : (el as HTMLElement).scrollTop);
    const onScroll = () => setOn(read() > threshold);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef, threshold]);

  const toTop = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    const el = scroller.current ?? window;
    // 움직임을 줄여 달라고 한 사용자에게는 애니메이션 없이 바로 올린다
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const opts: ScrollToOptions = { top: 0, behavior: reduce ? 'auto' : 'smooth' };
    (el as HTMLElement | Window).scrollTo(opts);
  }, [onClick]);

  return (
    <button
      ref={(node) => {
        (selfRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      }}
      type={type}
      aria-label={label}
      title={label}
      className={cx('scroll-top', !on && 'is-off', className)}
      onClick={toTop}
      {...props}
    >
      {/* 아이콘 프레임은 24(num-24) — Figma 아이콘 컴포넌트와 같은 치수다.
          글리프는 22 그대로라서 좌표를 중심 12 기준 22/24 로 줄여 둔다(프레임만 커지고 글리프는 안 커진다). */}
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 18.42V6.5" />
        <path d="M5.58 12 12 5.58 18.42 12" />
      </svg>
    </button>
  );
});
